// src/pages/RedeemCoupon.jsx
//
// Student-facing page: scan a coupon's QR with the camera (or type
// the code by hand), preview what it's worth, then confirm to redeem
// it — the value is credited to the student's wallet for that
// school's program, added on top of whatever balance was already
// there. Flow: scan/type -> preview (lookup) -> confirm -> redeemed.
//
// Camera scanning uses jsQR (tiny, dependency-free) to decode frames
// pulled from getUserMedia via a <video>/<canvas> pair. Install once:
//   npm install jsqr
// If the camera isn't available/permitted, manual code entry always
// works as a fallback.
import React, { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { useUser } from '../contexts/UserContext';
import { useCouponRedemption } from '../contexts/CouponRedemptionContext';
import RedeemCouponHeader from '../components/ui/RedeemCouponHeader';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';

// ─── Card Header (desktop / tablet) ──────────────────────────────────────────
// Icon is a vector glyph (not an emoji) to match MyWallet.jsx's CardHeader,
// so the two sibling pages read as the same visual family.

const CardHeader = () => (
  <div className="hidden sm:flex items-center gap-3 mb-5 pb-4 border-b-2 border-gray-400">
    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center shadow-sm">
      <svg
        viewBox="0 0 24 24"
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        aria-hidden="true"
      >
        <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 1 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 1 0 0-4V8Z" />
        <path d="M13 6v2M13 11v2M13 16v2" strokeLinecap="round" />
      </svg>
    </div>
    <div className="min-w-0">
      <h2 className="text-base font-bold text-gray-800 leading-tight">מימוש קופון גפ״ן</h2>
      <p className="text-xs text-gray-400 mt-0.5">סריקת קוד QR או הזנת קוד ידנית מוסיפה קרידט לתוכנית שלך</p>
    </div>
  </div>
);

const formatMoney = (v) => `₪${Number(v || 0).toLocaleString('he-IL', { maximumFractionDigits: 2 })}`;

const normalizeCode = (raw) => {
  // Accept either a bare code or a full deep-link URL the QR might
  // encode (e.g. https://app/redeem?code=XXXXX-XXXXX) — pull the code
  // out either way.
  const trimmed = (raw || '').trim();
  try {
    const asUrl = new URL(trimmed);
    const fromQuery = asUrl.searchParams.get('code');
    if (fromQuery) return fromQuery.toUpperCase();
  } catch {
    // not a URL — fall through to treating it as a bare code
  }
  return trimmed.toUpperCase();
};

const RedeemCoupon = () => {
  const { showAlert } = useUser();
  const { lookupCoupon, redeemCoupon } = useCouponRedemption();

  const [manualCode, setManualCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState('');

  const [preview, setPreview] = useState(null); // lookup() result, pending confirmation
  const [previewCode, setPreviewCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [result, setResult] = useState(null); // redeem() result, once committed
  const [detected, setDetected] = useState(false); // brief "caught it" flash before moving to preview

  const videoRef = useRef(null);
  const canvasRef = useRef(null); // hidden, full-resolution — used only to decode frames
  const overlayCanvasRef = useRef(null); // visible — draws the live tracking box over the QR
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const lockRef = useRef({ code: null, start: 0, lastSeen: 0 }); // tracks how long the SAME code has been held in view

  // How long a QR has to sit steadily in frame, once found, before it
  // actually gets scanned — this is the "focus, then scan" pause the
  // box is here to make visible: findQR -> box locks onto it and
  // fills in -> only then does the scan actually fire.
  const LOCK_MS = 400;
  // A single missed frame (camera autofocus hunting, a blink of
  // motion blur, glare) shouldn't throw away the progress made so
  // far — only treat the QR as actually lost, and restart the hold
  // from zero, once it's been gone for a real beat.
  const LOSS_GRACE_MS = 300;

  const stopScanning = () => {
    setScanning(false);
    setDetected(false);
    lockRef.current = { code: null, start: 0, lastSeen: 0 };
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const runPreview = async (rawCode) => {
    const code = normalizeCode(rawCode);
    if (!code) return;
    setChecking(true);
    setResult(null);
    try {
      const data = await lookupCoupon(code);
      // apiFetch only rejects on a non-2xx HTTP status; a server bug
      // that returns 200 with an error payload (ok:false, no real
      // fields) must still be treated as a failure here, not silently
      // shown as a valid preview.
      if (!data?.ok) {
        throw new Error(data?.error || 'הקוד שהוזן אינו תקין');
      }
      setPreview(data);
      setPreviewCode(code);
      stopScanning();
    } catch (err) {
      showAlert?.(err.message || 'הקוד שהוזן אינו תקין', 'warning');
    } finally {
      setChecking(false);
    }
  };

  const tick = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height);

    const overlay = overlayCanvasRef.current;
    if (overlay) {
      // Overlay canvas is sized to the video's actual displayed box
      // (not its native resolution), so the box we draw lines up with
      // what the person sees, not the raw camera frame.
      const dw = video.clientWidth || overlay.width || 1;
      const dh = video.clientHeight || overlay.height || 1;
      overlay.width = dw;
      overlay.height = dh;
      const octx = overlay.getContext('2d');
      octx.clearRect(0, 0, dw, dh);

      if (code?.location) {
        // jsQR's corner points are in the hidden canvas's native
        // resolution; the video is shown with object-cover, which
        // scales it up to fill the box and centre-crops the overflow —
        // map each point through that same scale+crop so the drawn box
        // lands exactly on the real QR on screen.
        const scale = Math.max(dw / canvas.width, dh / canvas.height);
        const offX = (canvas.width * scale - dw) / 2;
        const offY = (canvas.height * scale - dh) / 2;
        const toDisplay = (pt) => ({ x: pt.x * scale - offX, y: pt.y * scale - offY });
        const { topLeftCorner, topRightCorner, bottomRightCorner, bottomLeftCorner } = code.location;
        const pts = [topLeftCorner, topRightCorner, bottomRightCorner, bottomLeftCorner].map(toDisplay);

        // Step 1 of "focus, then scan": a QR is in view, so track how
        // long it's been the SAME code continuously — a fleeting or
        // half-visible read shouldn't fire a scan, only one that holds
        // steady does.
        const now = performance.now();
        if (lockRef.current.code !== code.data) {
          lockRef.current = { code: code.data, start: now, lastSeen: now };
        } else {
          lockRef.current.lastSeen = now;
        }
        const progress = Math.min((now - lockRef.current.start) / LOCK_MS, 1);

        // The box itself is the "focus" feedback: dashed amber while
        // still homing in (a fixed alpha here, not one that grows with
        // progress — that plus jsQR's frame-to-frame corner jitter is
        // what read as flickering), solid green once the hold
        // completes.
        octx.lineWidth = 3;
        octx.setLineDash(progress >= 1 ? [] : [9, 6]);
        octx.strokeStyle = progress >= 1 ? '#34d399' : 'rgba(250, 204, 21, 0.85)';
        octx.beginPath();
        octx.moveTo(pts[0].x, pts[0].y);
        pts.slice(1).forEach((p) => octx.lineTo(p.x, p.y));
        octx.closePath();
        octx.stroke();
        octx.setLineDash([]);
        if (progress > 0 && progress < 1) {
          octx.fillStyle = `rgba(52, 211, 153, ${0.12 * progress})`;
          octx.fill();
        }

        // Step 2: the hold completed — this is the actual "scan" that
        // was being focused on. Vibrate + flash a checkmark, then hand
        // off to the real lookup a beat later.
        if (progress >= 1) {
          if (navigator.vibrate) navigator.vibrate(80);
          setDetected(true);
          window.setTimeout(() => runPreview(code.data), 250);
          return;
        }
      } else if (lockRef.current.code && performance.now() - lockRef.current.lastSeen > LOSS_GRACE_MS) {
        // Genuinely gone for a real beat, not just a blip — restart
        // the hold from scratch on whatever's found next.
        lockRef.current = { code: null, start: 0, lastSeen: 0 };
      }
    }

    rafRef.current = requestAnimationFrame(tick);
  };

  const startScanning = async () => {
    setCameraError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      // Don't touch videoRef here: the <video> element only exists in
      // the DOM once `scanning` is true (it's behind a conditional
      // render below), so at this point videoRef.current is still
      // null — assigning srcObject here was a silent no-op, leaving
      // the video permanently black even though the camera stream
      // itself started fine. Attaching the stream is handled by the
      // effect below, which runs right after the <video> mounts.
      setScanning(true);
    } catch (err) {
      setCameraError('לא ניתן לגשת למצלמה. ניתן להזין את הקוד ידנית למטה.');
    }
  };

  // Attach the already-open camera stream to the <video> element once
  // it exists (i.e. right after `scanning` flips to true and React
  // mounts it), then start the scan loop.
  useEffect(() => {
    if (!scanning || !videoRef.current || !streamRef.current) return;
    const video = videoRef.current;
    video.srcObject = streamRef.current;
    video.play().catch(() => {});
    rafRef.current = requestAnimationFrame(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  useEffect(() => () => stopScanning(), []);

  const handleConfirmRedeem = async () => {
    setRedeeming(true);
    try {
      const data = await redeemCoupon(previewCode);
      // Same defensive check as runPreview: only a genuine ok:true
      // response (with real creditAdded/walletBalance/programName
      // fields) counts as a successful redemption — never trust HTTP
      // 200 alone.
      if (!data?.ok) {
        throw new Error(data?.error || 'מימוש הקופון נכשל');
      }
      setResult(data);
      setPreview(null);
      showAlert?.('הקופון מומש בהצלחה', 'success');
    } catch (err) {
      showAlert?.(err.message || 'מימוש הקופון נכשל', 'error');
    } finally {
      setRedeeming(false);
    }
  };

  const handleReset = () => {
    setPreview(null);
    setPreviewCode('');
    setResult(null);
    setManualCode('');
    setCameraError('');
    setDetected(false);
    lockRef.current = { code: null, start: 0, lastSeen: 0 };
  };

  return (
    // min-h-dvh (dynamic viewport height), not min-h-screen (100vh): on
    // mobile, 100vh is measured with the browser chrome collapsed and
    // doesn't update when the keyboard opens/the chrome expands, so the
    // page height kept jumping the moment the keyboard appeared. dvh
    // tracks the actual visible viewport instead.
    <div dir="rtl" className="min-h-dvh bg-gray-100">
      {checking && !preview && (
        <SpinnerOverlay variant="glow" text="בודק קוד..." dark={false} blur={false} />
      )}

      <div className="sm:hidden sticky top-0 z-10">
        <RedeemCouponHeader />
      </div>

      <main className="mx-auto max-w-2xl px-2 pt-2 pb-8 lg:py-10">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-md overflow-hidden">
          <div className="h-1 bg-gradient-to-l from-indigo-500 via-purple-500 to-blue-500" />

          <div className="p-4 sm:p-6">
            <CardHeader />

            {result ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-3xl shadow-lg shadow-emerald-500/30">
                  🎉
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-gray-500">הקופון מומש בהצלחה עבור</p>
                  <p className="text-lg font-bold text-gray-800">{result.programName}</p>
                </div>
                <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 p-4 space-y-1">
                  <p className="text-xs text-emerald-700">קרידט שנוסף</p>
                  <p className="text-3xl font-extrabold bg-gradient-to-l from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                    +{formatMoney(result.creditAdded)}
                  </p>
                  <p className="text-[11px] text-emerald-600">
                    נוסף ליתרה הקודמת · יתרה נוכחית: <b>{formatMoney(result.walletBalance)}</b>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleReset}
                  className="w-full sm:w-auto inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold px-5 py-2.5 shadow-sm hover:opacity-90 active:scale-[0.98] transition-all"
                >
                  מימוש קופון נוסף
                </button>
              </div>
            ) : preview ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-lg shrink-0 shadow-sm">
                    🎫
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] text-gray-400">אישור מימוש קופון</p>
                    <p className="text-base font-semibold text-gray-800 truncate">{preview.programName}</p>
                    {preview.supplierName && <p className="text-xs text-gray-500 truncate">{preview.supplierName}</p>}
                  </div>
                </div>

                <div className="rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 p-4 text-center">
                  <p className="text-xs text-indigo-700">שווי הקופון</p>
                  <p className="text-3xl font-extrabold bg-gradient-to-l from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    {formatMoney(preview.valueAmount)}
                  </p>
                </div>

                {!preview.redeemable ? (
                  <p className="text-xs text-red-600 text-center bg-red-50 border border-red-100 rounded-xl py-2">
                    {preview.status === 'REDEEMED' ? 'קופון זה כבר מומש' : preview.status === 'CANCELLED' ? 'קופון זה בוטל' : 'קופון זה אינו זמין למימוש עבורך'}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 text-center">הסכום יתווסף ליתרת הקרידט הקיימת שלך בתוכנית זו.</p>
                )}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="flex-1 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium py-2.5 hover:bg-gray-50 transition-colors"
                  >
                    ביטול
                  </button>
                  <button
                    type="button"
                    disabled={!preview.redeemable || redeeming}
                    onClick={handleConfirmRedeem}
                    className="flex-1 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold py-2.5 shadow-sm hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {redeeming ? 'ממש…' : 'מימוש קופון'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Section label matches MyWallet.jsx's section-header
                    pattern (text-xs font-semibold text-gray-500) so the
                    two pages read as one family. */}
                <section className="space-y-2">
                  <h3 className="text-xs font-semibold text-gray-500">סריקת קוד QR</h3>

                  <div className="space-y-3">
                    {scanning ? (
                      <div className="relative mx-auto max-w-[280px] rounded-2xl overflow-hidden p-[2px] bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/20">
                        <div className="relative rounded-[14px] overflow-hidden bg-black">
                          <video ref={videoRef} className="w-full aspect-square object-cover" muted playsInline />
                          <canvas ref={canvasRef} className="hidden" />

                          {/* Draws the live tracking box exactly over the QR
                              once one is found — amber while the hold is still
                              filling in, green once it's locked — so scanning
                              visibly happens in two beats: find it, focus on
                              it, then (a moment later) it's scanned. */}
                          <canvas ref={overlayCanvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />

                          {/* Generic aiming guide, shown until a QR is actually
                              found and the tracking box above takes over. */}
                          <div
                            className={`absolute inset-6 rounded-xl pointer-events-none transition-colors duration-150 ${
                              detected ? 'border-4 border-emerald-400' : 'border-2 border-white/60'
                            }`}
                          />

                          {detected && (
                            <div className="absolute inset-0 flex items-center justify-center bg-emerald-500/20">
                              <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center text-white text-2xl shadow-lg animate-[pop-in_0.2s_ease-out]">
                                ✓
                              </div>
                            </div>
                          )}

                          <style>{`
                            @keyframes pop-in {
                              0% { transform: scale(0.4); opacity: 0; }
                              100% { transform: scale(1); opacity: 1; }
                            }
                          `}</style>

                          <button
                            type="button"
                            onClick={stopScanning}
                            className="absolute top-2 left-2 bg-black/50 backdrop-blur text-white text-xs px-2.5 py-1 rounded-lg hover:bg-black/70 transition-colors"
                          >
                            עצירה
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={startScanning}
                        className="group relative mx-auto block w-full max-w-[280px] aspect-square rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-indigo-300 transition-colors overflow-hidden"
                      >
                        {/* Inactive "viewfinder" look — a dark preview window
                            the person taps to turn the camera on, rather than
                            a plain call-to-action button. */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-gray-400 group-hover:text-indigo-500 transition-colors">
                          <span className="text-3xl">📷</span>
                          <span className="text-xs font-medium">לחצו להפעלת המצלמה</span>
                        </div>
                        <div className="absolute inset-6 rounded-xl border-2 border-gray-300/70 group-hover:border-indigo-300 pointer-events-none transition-colors" />
                      </button>
                    )}
                    {cameraError && <p className="text-[11px] text-red-600 text-center">{cameraError}</p>}
                    {checking && (
                      <p className="text-[11px] text-indigo-500 text-center flex items-center justify-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
                        בודק קוד…
                      </p>
                    )}
                  </div>
                </section>

                <section className="space-y-2">
                  <h3 className="text-xs font-semibold text-gray-500">הזנת קוד ידנית</h3>
                  <div className="rounded-xl border border-gray-100 p-4">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={manualCode}
                        onChange={(e) => setManualCode(e.target.value)}
                        placeholder="XXXXX-XXXXX"
                        // text-base (16px) on purpose: below that, iOS Safari
                        // auto-zooms in the moment this field gets focus,
                        // which is what reads as the whole page suddenly
                        // going "liquid"/unstable — it's the browser zooming
                        // and panning to the input, not a layout bug.
                        className="flex-1 rounded-xl border border-gray-200 px-3 py-2.5 text-base sm:text-sm font-mono tracking-widest text-center uppercase focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-shadow"
                      />
                      <button
                        type="button"
                        disabled={!manualCode.trim() || checking}
                        onClick={() => runPreview(manualCode)}
                        className="rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-medium px-5 shadow-sm hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        בדיקה
                      </button>
                    </div>
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default RedeemCoupon;